import { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { ArenaAugment } from "@outplayed/tooltips";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { ArenaSearch } from "./ArenaSearch";
import type { OverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";

interface ArenaAugmentsProps {
  className?: string;
  championId: number;
  data: OverviewInfo["arena_augments"];
  patch?: string;
}

export function ArenaAugments(props: ArenaAugmentsProps) {
  const { className, championId, data, patch } = props;
  const scrollable = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [scrollPadding, setScrollPadding] = useState(data.length >= 5 ? 14 : 0);
  const { getChampionName, useRiotArenaAugments } = getRiotAssetsContext();
  const { data: augmentsData } = useRiotArenaAugments({ ssr: true, patch });

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const sortedReference = useMemo(() => {
    let lookup: { [key: number]: number } = {};
    data
      .sort((a, b) => b.matches - a.matches)
      .forEach((augment, index) => {
        lookup[augment.augmentId] = index;
      });
    return lookup;
  }, [data]);

  const sortedData = useMemo(() => {
    return data
      .filter((augment) => {
        const augmentData = augmentsData?.[augment.augmentId];
        const { name = "" } = augmentData || {};
        const filterValue = searchValue.toLowerCase().replace(/ /g, "");
        const filterAugmentName = name.replace(/[^a-zA-Z0-9]/g, "");

        if (augment.augmentId === 0) return false;
        else if (!filterValue) return true;
        return filterAugmentName.toLowerCase().indexOf(filterValue) === 0;
      })
      .sort((a, b) => b.matches - a.matches);
  }, [data, searchValue]);

  useEffect(() => {
    setScrollPadding((scrollable.current?.offsetWidth || 0) - (scrollable.current?.clientWidth || 0));
  }, []);

  const shouldApplyScrollPadding = data.length > 5 && sortedData.length <= 5;

  return (
    <div className={classnames("arena-augments bg-purple-400", className)}>
      <div className="relative flex items-center justify-between leading-[20px] pl-[18px] pr-[18px] max-xxs:pl-[12px] h-[56px]">
        <HeaderBullet>
          <div className="flex-1">{getChampionName(championId)} Augments</div>
        </HeaderBullet>
        <ArenaSearch className="w-[170px]" placeholder="Search Augment" value={searchValue} onChange={searchOnChange} />
      </div>
      <div className="synergies-table text-[12px] font-medium">
        <div
          style={{ paddingRight: scrollPadding }}
          className="table_header flex max-xxs:pl-[12px] pl-[18px] pt-[12px] pb-[12px] border-t-[1px] border-b-[1px] border-purple-500 bg-purple-100"
        >
          <div className="flex-1 text-lavender-50">Augment</div>
          <div className="flex items-center basis-[80px] justify-center text-lavender-50">Pick Rate</div>
        </div>
        <div ref={scrollable} className="relative h-[230px] overflow-y-auto">
          <div
            className="absolute left-0 right-0 flex flex-col gap-[16px] pt-[8px] pb-[8px]"
            style={{ paddingRight: shouldApplyScrollPadding ? scrollPadding : 0 }}
          >
            {sortedData.map((augment, index) => {
              const { augmentId, pickRate, matches } = augment;

              return (
                <div key={augmentId} className="flex">
                  <div className="flex flex-1 items-center pl-[18px] max-xxs:pl-[8px]">
                    <div className="mr-[8px] w-[16px] text-center text-lavender-50">{sortedReference[augmentId]}</div>
                    <div className="w-[30px] h-[30px] rounded-[2px] bg-purple-200">
                      <ArenaAugment augmentId={augmentId} patch={patch} className="w-[100%]" />
                    </div>
                    <div className="flex-1 ml-[12px] font-bold">{augmentsData?.[augmentId]?.name || ""}</div>
                  </div>
                  <div className="flex flex-col items-center basis-[80px] justify-center text-lavender-50">
                    <span className="font-bold text-white leading-none">{pickRate.toFixed(2)}%</span>
                    <span className="text-[11px]">{numberWithCommas(matches)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
